<template>
  <div>
    <app-spinner v-if="!is_loaded"/>
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1 class="title">{{ arrival_data.climbing_group.name }} ({{ arrival_data.pax }})</h1>
          <h2 class="title">Managers:</h2>
          <div v-for="manager of arrival_data.managers" :key="manager.id" class="manager-title">
            <div class="manager-title__photo">
              <img :src="getUserAvatar(manager.crop.crop_url)" alt="Avatar">
            </div>
            <span class="manager-title__name">{{ manager.full_name }}</span>
          </div>
        </app-cells>
        <app-button
          v-if="is_general_manager || is_operations_supervisor"
          theme="error"
          @click.native="$modal.show('delete-modal')"
        >
          Delete
        </app-button>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Date:</span>
              <span>{{ dateFormat(arrival_data.date) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Location:</span>
              <span>{{ arrival_data.location.name }}</span>
            </div>
          </div>
        </div>
      </div>
      <v-client-table
        :data="table_clients.items"
        :columns="table_clients.columns"
        :options="table_clients.options"
        class="table-default"
      >
        <template #h__full_name><div class="table-default__left">Full name</div></template>
        <template #h__group><div class="table-default__left">Group</div></template>
        <template #h__hotel><div class="table-default__left">Hotel</div></template>
        <template #full_name="{ row }">
          <div class="table-default__left table-default__left--photo">
            <span
              :class="`fflag ff-md fflag-${row.client.client.citizenship ? row.client.client.citizenship.code : 'empty'}`"
              v-tooltip="{ content: row.client.citizenship ? row.client.client.citizenship.name : 'Not provided', placement: 'top-start', offset: 10 }"
            ></span>
            <template v-if="row.client.client.gender">
              <img v-if="row.client.client.gender.code === 'male'" src="@/assets/img/male-icon.svg" alt="">
              <img v-if="row.client.client.gender.code === 'female'" src="@/assets/img/female-icon.svg" alt="">
            </template>
            <img v-else src="@/assets/img/gender-plug-icon.svg" alt="">
            <router-link
              :to="{ name: 'clients-detail', params: { id: row.client.client.id } }"
              class="table-default__link-border"
            >
              {{ row.client.client.full_name }}
            </router-link>
            <span v-if="row.client.age">({{ row.client.age }} y.o.)</span>
          </div>
        </template>
        <template #group="{ row }">
          <div class="table-default__controls table-default__left">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.client.client.manager.crop.crop_url)" alt=""
                v-tooltip="{ content: row.client.client.manager.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
            <template v-if="row.client.client.group">
              <router-link
                :to="{ name: 'clients-groups-general', params: { id: row.client.client.group.id } }"
                class="table-default__link-border"
              >
                {{ row.client.client.group.name }}
              </router-link>
            </template>
            <div v-else class="table-default__left">-</div>
          </div>
        </template>
        <template #hotel="{ row }">
          <div class="table-default__left">
            <div v-if="row.client.after">
              {{ row.client.after.hotel_name }} ({{ row.client.after.room_name }})
            </div>
            <template v-else>-</template>
            <div v-if="row.client?.after?.hotel_management_room_short_name">Room: <b>{{ row.client.after.hotel_management_room_short_name }}</b></div>
          </div>
        </template>
        <template #safari="{ row }">
          <template v-if="row.client.client.safari.length">
            <template v-if="row.client.client.safari.length > 1">
              <span class="status-tag" style="background-color: #e85c20">
                {{ row.client.client.safari.length }}
              </span>
            </template>
            <template v-else>
              <div class="table-default__controls">
                <span class="table-default__activity table-default__activity--active table-default__activity--safari"></span>
                <div>
                  {{ row.client.client.safari[0].short_itinerary }}<br>
                  {{ dateFormat(row.client.client.safari[0].tour_start) }}
                </div>
              </div>
            </template>
          </template>
          <template v-else>-</template>
        </template>
        <template #departure="{ row }">
          <template v-if="row.client.client.transport.departure.departure_method">
            <div class="table-default__controls" style="margin-bottom: 10px;">
              <span
                class="table-default__transport table-default__transport--arrival"
                :class="{
                'table-default__transport--present': row.client.client.transport.departure.departure_method,
                'table-default__transport--confirmed': row.client.client.transport.departure.departure_confirmed,
                'table-default__transport--car': row.client.client.transport.departure.departure_method?.code === 'car',
                'table-default__transport--bus': row.client.client.transport.departure.departure_method?.code === 'bus',
                'table-default__transport--self': row.client.client.transport.departure.departure_method?.code === 'self'
              }"
              ></span>
              <div>
                {{ row.client.client.transport.departure.departure_date ? dateFormat(row.client.client.transport.departure.departure_date) : '-' }}
                {{ row.client.client.transport.departure.departure_time ? row.client.client.transport.departure.departure_time : '' }}
              </div>
              <div>
                {{ row.client.client.transport.departure.departure_location ? row.client.client.transport.departure.departure_location.short_name : '-' }}
              </div>
            </div>
            <br>
            <div v-if="row.client.client.transfers?.departure.length" class="table-default__controls">
              <img src="@/assets/img/departure-modal-icon.svg" alt="Transfers">
              <div>
                {{ dateFormat(row.client.client.transfers.departure[0].pick_up_date) }} {{ row.client.client.transfers.departure[0].pick_up_time }}
              </div>
            </div>
            <span v-else class="status-tag" style="background-color: #e85c20">
              No transfer
            </span>
          </template>
          <template v-else>-</template>
        </template>
        <template #notes="{ row }">{{ row.client.arrival_notes || '-' }}</template>
      </v-client-table>
      <template v-if="table_in_process.items.length">
        <app-cells><h2 class="title">Rentals</h2></app-cells>
        <v-client-table
          :data="table_in_process.items"
          :columns="table_in_process.columns"
          :options="table_in_process.options"
          class="table-default"
          :class="{ 'table-default--loading': table_in_process_preloader }"
        >
          <template #h__id><div class="table-default__left">ID</div></template>
          <template #h__client><div class="table-default__left">Client</div></template>
          <template #status="{ row }">
          <span class="status-tag" :style="{ 'backgroundColor': row.status.color }">
            {{ row.status.name }}
          </span>
          </template>
          <template #id="{ row }">
            <div class="table-default__left">
              <router-link :to="{name: 'rentals-detail', params: {id: row.id }}" class="table-default__link-border">
                Rental - {{ row.id }}
              </router-link>
            </div>
          </template>
          <template #client="{ row }">
            <div class="table-default__left">
              <router-link v-if="row.client" :to="{name: 'clients-detail', params: {id: row.client.client.id }}" class="table-default__link-border">
                {{ row.client.client.full_name }}
              </router-link>
              <template v-else>-</template>
            </div>
          </template>
          <template #amount="{ row }">
          <span
            v-if="row.discount_amount && row.discount_amount > 0"
            class="status-tag"
            :style="{ 'backgroundColor': '#77b017' }"
            v-tooltip="{ content: `Discount: ${row.discount_amount}$<br>${row.discount_description || ''}`, placement: 'left', offset: 10, html: true }"
          >
            {{ row.amount }}$
          </span>
            <span
              v-else
              v-tooltip="{ content: amountTooltipContent(row), placement: 'left', offset: 10, html: true }"
            >
            {{ row.amount }}$
          </span>
          </template>
          <template #balance="{ row }">
          <span
            class="status-tag"
            :style="{ 'backgroundColor': row.balance === 0 ? '#77b017' : row.balance > 0 ? '#f0c427' :'#e85c20' }"
          >
            {{ row.balance === 0 ? 'Paid': `${row.balance}$` }}
          </span>
          </template>
          <template #returned="{ row }">
          <span
            class="status-tag"
            :style="{ 'backgroundColor': row.rental_items === row.rental_items_returned ? '#77b017' : '#e85c20' }"
          >
            {{ row.rental_items }} / {{ row.rental_items_returned }}
          </span>
          </template>
          <template #rental_start="{ row }">
            {{ dateFormat(row.rental_start) }}
          </template>
          <template #rental_end="{ row }">
          <span
            v-tooltip="{ content: 'Rental end expired', placement: 'top-start', offset: 10 }"
            v-if="row.end_expired"
            class="status-tag"
            :style="{ 'backgroundColor': '#e85c20' }"
          >
            {{ dateFormat(row.rental_end) }}
          </span>
            <span v-else>{{ dateFormat(row.rental_end) }}</span>
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationProcess"
              v-if="pagination_in_process.pages > 1"
              v-model="pagination_in_process.page"
              :page-count="pagination_in_process.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_in_process.count" :count="table_in_process.items.length" :page="pagination_in_process.page" />
        </app-cells>
      </template>
    </template>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete arrival
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this arrival?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteArrival" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { deleteClimbingArrivalsDetail, getClimbingArrivalsClients, getClimbingArrivalsDetail, getRentals } from '@/http'
import { format, parseISO } from 'date-fns'

export default {
  name: 'ClimbingArrivalsDetail',
  data() {
    return {
      is_loaded: false,
      arrival_data: {},
      table_clients: {
        items: [],
        columns: ['full_name', 'hotel', 'notes', 'safari', 'departure', 'group'],
        options: {
          headings: {
            full_name: 'Full name',
            group: 'Group',
            hotel: 'Hotel',
            safari: 'Safari',
            departure: 'Departure',
            notes: 'Notes',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_in_process: {
        items: [],
        columns: ['status', 'id', 'client', 'amount', 'balance', 'returned', 'rental_start', 'rental_end'],
        options: {
          headings: {
            'status': 'Status',
            'id': 'ID',
            'client': 'Client',
            'amount': 'Amount',
            'balance': 'Balance',
            'returned': 'Returned',
            'rental_start': 'Start',
            'rental_end': 'End',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_in_process: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_in_process_preloader: false,
    }
  },
  created() {
    Promise.all([
      getClimbingArrivalsDetail(this.$route.params.id),
      getClimbingArrivalsClients(this.$route.params.id),
      getRentals({
        page: this.pagination_in_process.page,
        page_size: 20,
        arrivals: this.$route.params.id })
    ])
      .then(res => {
        this.arrival_data = res[0].data
        this.table_clients.items = res[1].data
        this.table_in_process.items = res[2].data.results
        this.pagination_in_process.pages = res[2].data.pages
        this.pagination_in_process.count = res[2].data.count
        this.is_loaded = true
      })
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_operations_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_supervisor')
    }
  },
  methods: {
    fetchItemsProcess() {
      const params = {
        page: this.pagination_in_process.page,
        page_size: 20,
        arrivals: this.$route.params.id
      }
      getRentals(params)
        .then(res => {
          this.table_in_process.items = res.data.results
          this.pagination_in_process.pages = res.data.pages
          this.pagination_in_process.count = res.data.count
          this.is_loaded = true
          this.table_in_process_preloader = false
        })
    },
    onClickPaginationProcess(page) {
      this.table_in_process_preloader = true
      this.pagination_in_process.page = page
      this.fetchItemsProcess()
    },
    onDeleteArrival() {
      this.$refs.delete.preload = true
      deleteClimbingArrivalsDetail(this.$route.params.id)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Arrival deleted successfully',
          })
          this.$router.push({name: 'climbing-briefings-list'})
        })
        .catch(() => {
          this.$refs.delete.preload = false
        })
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    dateFormat(date) {
      if (!date) return ''
      return format(parseISO(date), 'dd MMM yyyy')
    },
    amountTooltipContent(data) {
      let content = `Rental: ${data.amount_rental}$<br>Purchase: ${data.amount_sale}$`
      if (data.resupply_fee) content += '<br>Resupply: 50$'
      if (data.discount_method) content += `<br>Discount: ${data.discount_method.code === 'basic' ? `${data.discount}$` : `${data.discount}%`}`
      return content
    },
  }
}
</script>
